<template>
  <onboarding-form-layout
    :title="$t('incremental_onboarding.registration.title')"
    description="Upload document"
    :loading="submitting"
    :show-previous="false"
    @proceed="proceed"
    @previous="previous"
    :disable-proceed="disableProceed"
  >
    <ErrorCodeNotification v-if="errorCodes && errorCodes.length > 0" :error-codes="errorCodes" />
    <div class="upload-doc-container">
      <!-- Add notification banner as per figma -->
      <div class="upload-header">
        {{ $t('uploadDocument.uploadHeader') }}
      </div>
      <div v-for="failureReasonCode in failureReasonCodes" :key="failureReasonCode" style="padding-bottom: 12px">
        <SelectField
          :label="$t('Select Document')"
          :searchable="false"
          :clearable="false"
          :value="getSelectedDocumentType(failureReasonCode)"
          :options="getDocumentList(failureReasonCode)"
          :on-update="(value) => onDocumentTypeChange(value, failureReasonCode)"
          select-classname="document-select"
        />
        <div class="upload-box-container">
          <div
            class="upload-box-sub-container"
            v-for="uploadOption in uploadOptions[failureReasonCode] || []"
            :key="uploadOption"
          >
            <div class="upload-box-header">
              {{ $t(`uploadDocument.${uploadOption}`) }}
            </div>
            <UploadBox
              :upload-type="uploadOption"
              :upload-complete="() => uploadCompleteHandler()"
              :failure-code="failureReasonCode"
            />
          </div>
        </div>
      </div>
    </div>
  </onboarding-form-layout>
</template>

<script>
import SelectField from "@/components/common/SelectField.vue";
import UploadBox from "@/components/UploadBox.vue";
import { mapActions, mapGetters } from "vuex";
import OnboardingFormLayout from "@/components/incremental-onboarding/OnboardingFormLayout.vue";
import Merchants from "@/api/merchants";
import ErrorCodeNotification from "@/components/incremental-onboarding/ErrorCodeNotification.vue";
import FullStoryHelper from '@/app/utils/fullstory';
import { DOCUMENT_UPLOAD_PROCEED } from '@/app/utils/fullstory/constants';

export default {
  name: "IncrementalDocumentUpload",
  components: { ErrorCodeNotification, OnboardingFormLayout, SelectField, UploadBox},
  props: {
    failureReasonCodes: {
      type: Array,
      required: true,
    },
    errorCodes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data: () => ({
    uploadOptions: {},
    uploadedFiles: [],
    merchantFiles: [],
    selectedDocumentTypes: {},
    submitting: false,
    disableProceed: true,
  }),
  computed: {
    ...mapGetters('stakeholdersDetails', [
      'getControlPerson',
      'getControlPersonName',
      'getControlPhoneNumber',
    ]),
    ...mapGetters('app', [
      'getMerchantId',
      'getPayfacErrorCodeMapper',
    ]),
    ...mapGetters('onboarding', [
      'getMerchantUploadedDocuments',
    ]),
  },
  mounted() {
    this.failureReasonCodes.forEach(failureReasonCode => {
      this.onDocumentTypeChange(this.getDocumentList(failureReasonCode)[0]?.value, failureReasonCode)
    });
  },
  methods: {
    ...mapActions('onboarding', ['clearMerchantUploadedDocuments']),
    getSelectedDocumentType(failureReasonCode) {
      return (
        this.selectedDocumentTypes[failureReasonCode] ||
        this.getDocumentList(failureReasonCode)[0]?.value
      );
    },
    getDocumentList: function (failureCode) {
      return this.getPayfacErrorCodeMapper.ErrorCodeToDocumentType[
        this.getPayfacErrorCodeMapper.getRawErrorCode(failureCode)
        ]?.map(x => {
        return {
          displayText: this.$t(`documentType.${x}`),
          value: x,
        }
      }) || [];
    },
    onDocumentTypeChange(documentType, failureReasonCode) {
      this.$set(this.selectedDocumentTypes, failureReasonCode, documentType);
      this.uploadOptions[failureReasonCode] = this.getPayfacErrorCodeMapper.DocumentTypePages[documentType] || [];
      // Changing the document type invalidates any pages already uploaded for this requirement only.
      const newUploadedDocuments = this.getMerchantUploadedDocuments.filter(doc => doc.failure_code !== failureReasonCode);
      this.clearMerchantUploadedDocuments(newUploadedDocuments);
    },
    // A requirement is satisfied once every page of its selected document type is uploaded
    // (e.g. an ID card needs both front and back).
    isRequirementSatisfied(failureReasonCode) {
      const requiredPages = this.uploadOptions[failureReasonCode] || [];
      if (requiredPages.length === 0) {
        return false;
      }
      const uploadedFileTypes = new Set(
        this.getMerchantUploadedDocuments
          .filter(doc => doc.failure_code === failureReasonCode)
          .map(doc => doc.file_type)
      );
      return requiredPages.every(page => uploadedFileTypes.has(page));
    },
    uploadCompleteHandler: function () {
      this.disableProceed = !this.failureReasonCodes.every(code => this.isRequirementSatisfied(code));
    },

    async proceed() {
      try {
        this.submitting = true;

        // Emit FullStory event - document upload is always considered an update operation
        FullStoryHelper.event(DOCUMENT_UPLOAD_PROCEED);

        await Merchants.asyncFileUpload(this.getMerchantId, this.getMerchantUploadedDocuments);
        await this.clearMerchantUploadedDocuments([]);
        this.$emit('proceed')
      } catch (error) {
        await this.clearMerchantUploadedDocuments(this.getMerchantUploadedDocuments);
        this.$danger(this.$t('errors.submitOnboardingFailed') + " - " + error.source?.detail);
      } finally {
        this.submitting = false;
      }
    },
    previous() {
      this.clearMerchantUploadedDocuments([]);
      this.$emit('previous');
    },
  },
};
</script>

<style lang="scss">
.upload-doc-container {
  text-align: left;
  width: 100%;
  max-width: 700px;
  align-items: start;
  display: flex;
  flex-direction: column;
  margin: auto;
  padding-bottom: 0 !important;

  & .header {
    font-weight: $weight_bold;
    font-size: 24px;
    color: $text-color;
    text-align: left;
    margin-bottom: 8px;
  }

  & .subtitle {
    font-size: 12px;
  }

  & .separator {
    background-color: $divider_color;
    height: 1px;
    margin: 20px 0 20px 0;
    width: 100%;
  }

  & .error-container {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 16px;
    background: #FEF2F2;
    border-radius: 8px;
    width: 100%;
  }

  & .error-title-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  & .red-info {
    margin: 5px 15px 0 0;
  }

  & .error-header {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: #1F2937;
    flex: none;
    order: 0;
    flex-grow: 0;
    margin: 4px 0px;
  }

  & .error-subtitle {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    color: #1F2937;
    flex: none;
    order: 1;
    flex-grow: 0;
    margin: 4px 0px;
  }

  & .user-container {
    background: #F3F4F6;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 20px 16px 20px 16px;
    width: 100%;
    margin-top: 30px;
  }

  & .user-name {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 28px;
    color: $neutral_800;
  }

  & .user-designation {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: $neutral_400;
    margin-bottom: 12px;
    margin-top: 5px;
  }

  & .user-details-container {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin: 3px 0;
  }

  & .user-details {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: $neutral_400;
    margin-right: 15px;
  }

  & .ownership-header {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    color: $neutral_800;
    margin: 24px 0 12px 0;
  }

  & .upload-container {
    display: flex;
    flex-direction: row;
    margin-top: 40px;
    width: 100%;
  }

  & .upload-details-container {
    display: flex;
    flex-direction: row;
    width: 100%;
  }

  & .upload-header {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: $neutral_800;
    flex: 1;
    padding-bottom: 16px;
  }

  & .data-security {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: $neutral_400;
    flex: 1;
    display: flex;
    flex-direction: row;
    width: 100%;

    & .lock {
      flex: 1;
      text-align: right;
      align-items: end;
      margin-right: 4px;

      & img {
        width: 12px;
      }
    }
  }

  & .upload-subtitle {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: $neutral_400;
    margin-top: 5px;
  }

  & .document-select {
    width: 300px;
    display: block;
  }

  & .upload-box-container {
    display: flex;
    flex-direction: row;
    margin-top: 20px;
    width: 100%;

    @media screen and (max-width: 600px) {
      display: flex;
      flex-direction: column;
    }
  }

  & .upload-box-sub-container {
    display: flex;
    flex-direction: column;
    flex: 1;

    @media screen and (max-width: 600px) {
      &:first-child {
        margin-bottom: 20px;
      }
    }

    @media screen and (min-width: 600px) {
      &:first-child {
        margin-right: 20px;
      }
    }
  }

  & .upload-box-header {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: $neutral_400;
    margin-bottom: 10px;
  }

  & .loading-already-uploaded {
    margin: auto;
    margin-top: 10px;
    font-size: 14px;
  }

  & .already-uploaded {
    margin-top: 20px;
    background: #F3F4F6;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    padding: 20px 16px 20px 16px;
    width: 100%;
  }

  & .already-uploaded-title {
    font-size: 16px;
    font-weight: $weight-bolder;
  }

  & .merchant-file-list {
    display: flex;
    width: 100%;
    margin: 20px 0 10px 0;

    & .upload-status {
      display: flex;
      & img {
        margin-right: 10px;
      }
    }
  }

}


</style>